import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { PlayCircle, Plus, Trash2, Edit, Search, Eye, Heart, ExternalLink, X, Save, MapPin, Car, Newspaper, Link as LinkIcon, Loader } from 'lucide-react'

interface Video {
    id: number
    video_url: string
    thumbnail_url: string
    title?: string
    views: number
    likes: number
    status: string
    linked_item_type?: string
    linked_item_id?: number
    created_at: number
}

interface LinkedItem {
    id: number
    name: string
    type: string
}

interface FormData {
    video_url: string
    title_en: string
    title_vi: string
    status: string
    linked_item_type: string
    linked_item_id: string
    linked_item_name: string
}

export default function VideosManagement() {
    const [videos, setVideos] = useState<Video[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [search, setSearch] = useState('')
    const [showModal, setShowModal] = useState(false)
    const [editingVideo, setEditingVideo] = useState<Video | null>(null)
    const [formData, setFormData] = useState<FormData>({
        video_url: '',
        title_en: '',
        title_vi: '',
        status: 'published',
        linked_item_type: 'none',
        linked_item_id: '',
        linked_item_name: ''
    })

    // Linked item search state
    const [linkedItemSearch, setLinkedItemSearch] = useState('')
    const [linkedItemResults, setLinkedItemResults] = useState<LinkedItem[]>([])
    const [searchingLinkedItems, setSearchingLinkedItems] = useState(false)

    useEffect(() => {
        fetchVideos()
    }, [])

    // Search linked items when type or search changes
    useEffect(() => {
        if (formData.linked_item_type !== 'none' && linkedItemSearch.length >= 2) {
            searchLinkedItems()
        } else {
            setLinkedItemResults([])
        }
    }, [linkedItemSearch, formData.linked_item_type])

    const fetchVideos = async () => {
        setLoading(true)
        try {
            const res = await fetch('/api/shorts?limit=50')
            const data = await res.json()
            if (data.success) {
                setVideos(data.data || [])
            }
        } catch (error) {
            console.error('Failed to fetch videos:', error)
        } finally {
            setLoading(false)
        }
    }

    const searchLinkedItems = async () => {
        setSearchingLinkedItems(true)
        try {
            let endpoint = ''
            switch (formData.linked_item_type) {
                case 'tour':
                    endpoint = `/api/tours?search=${encodeURIComponent(linkedItemSearch)}&limit=10`
                    break
                case 'vehicle':
                    endpoint = `/api/vehicles?search=${encodeURIComponent(linkedItemSearch)}&limit=10`
                    break
                case 'article':
                    endpoint = `/api/news?search=${encodeURIComponent(linkedItemSearch)}&limit=10`
                    break
            }

            if (endpoint) {
                const res = await fetch(endpoint)
                const data = await res.json()
                const items = data.data || data.vehicles || data.tours || data.articles || []

                setLinkedItemResults(items.slice(0, 10).map((item: any) => {
                    // Handle different API response structures
                    let name = ''

                    // For vehicles/tours with nested translations
                    if (item.translations?.en?.title) {
                        name = item.translations.en.title
                    }
                    // For articles with flat structure
                    else if (item.title_en) {
                        name = item.title_en
                    }
                    // For vehicles with brand/model
                    else if (item.brand && item.model) {
                        name = `${item.brand} ${item.model}`
                    }
                    // For tours with name fields
                    else if (item.name_en) {
                        name = item.name_en
                    }
                    else if (item.name) {
                        name = item.name
                    }
                    else if (item.title) {
                        name = item.title
                    }

                    return {
                        id: item.id,
                        name: name || `Item #${item.id}`,
                        type: formData.linked_item_type
                    }
                }))
            }
        } catch (error) {
            console.error('Failed to search linked items:', error)
        } finally {
            setSearchingLinkedItems(false)
        }
    }

    const deleteVideo = async (id: number) => {
        if (!confirm('Delete this video?')) return
        try {
            const res = await fetch(`/api/admin/videos/${id}`, { method: 'DELETE' })
            if ((await res.json()).success) {
                setVideos(prev => prev.filter(v => v.id !== id))
            }
        } catch (error) {
            console.error('Failed to delete:', error)
        }
    }

    const handleEdit = (video: Video) => {
        setEditingVideo(video)
        setFormData({
            video_url: video.video_url || '',
            title_en: video.title || '',
            title_vi: '',
            status: video.status || 'published',
            linked_item_type: video.linked_item_type || 'none',
            linked_item_id: video.linked_item_id?.toString() || '',
            linked_item_name: video.linked_item_id ? `ID: ${video.linked_item_id}` : ''
        })
        setLinkedItemSearch('')
        setLinkedItemResults([])
        setShowModal(true)
    }

    const handleAdd = () => {
        setEditingVideo(null)
        setFormData({
            video_url: '',
            title_en: '',
            title_vi: '',
            status: 'published',
            linked_item_type: 'none',
            linked_item_id: '',
            linked_item_name: ''
        })
        setLinkedItemSearch('')
        setLinkedItemResults([])
        setShowModal(true)
    }

    const selectLinkedItem = (item: LinkedItem) => {
        setFormData({
            ...formData,
            linked_item_id: item.id.toString(),
            linked_item_name: item.name
        })
        setLinkedItemSearch('')
        setLinkedItemResults([])
    }

    // Convert YouTube shorts URL to watch URL
    const normalizeYoutubeUrl = (url: string): string => {
        // Convert youtube.com/shorts/VIDEO_ID to youtube.com/watch?v=VIDEO_ID
        const shortsMatch = url.match(/youtube\.com\/shorts\/([^?&]+)/)
        if (shortsMatch) {
            return `https://www.youtube.com/watch?v=${shortsMatch[1]}`
        }
        return url
    }

    const handleVideoUrlChange = (url: string) => {
        const normalizedUrl = normalizeYoutubeUrl(url)
        setFormData({ ...formData, video_url: normalizedUrl })
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setSaving(true)

        try {
            const url = editingVideo
                ? `/api/admin/videos/${editingVideo.id}`
                : '/api/admin/videos'

            const res = await fetch(url, {
                method: editingVideo ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    video_url: formData.video_url,
                    title_en: formData.title_en,
                    title_vi: formData.title_vi,
                    status: formData.status,
                    linked_item_type: formData.linked_item_type,
                    linked_item_id: formData.linked_item_id ? parseInt(formData.linked_item_id) : null
                })
            })

            const data = await res.json()
            if (data.success) {
                setShowModal(false)
                fetchVideos()
            } else {
                alert(data.error || 'Failed to save')
            }
        } catch (error) {
            console.error('Save error:', error)
            alert('Failed to save video')
        } finally {
            setSaving(false)
        }
    }

    const formatNumber = (num: number) => {
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
        return num?.toString() || '0'
    }

    const getYoutubeThumbnail = (url: string) => {
        const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/)
        if (match) return `https://i.ytimg.com/vi/${match[1]}/hqdefault.jpg`
        return url
    }

    const getLinkedItemIcon = (type?: string) => {
        switch (type) {
            case 'tour': return <MapPin className="w-3 h-3 text-green-500" />
            case 'vehicle': return <Car className="w-3 h-3 text-blue-500" />
            case 'article': return <Newspaper className="w-3 h-3 text-purple-500" />
            default: return null
        }
    }

    const filteredVideos = videos.filter(v =>
        v.title?.toLowerCase().includes(search.toLowerCase()) ||
        v.video_url.toLowerCase().includes(search.toLowerCase())
    )

    return (
        <>
            <Helmet><title>Videos Management | Admin</title></Helmet>

            <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <PlayCircle className="w-6 h-6 text-primary-600" />
                            Short Videos Management
                        </h1>
                        <p className="text-gray-600">Manage short videos for the Shorts page</p>
                    </div>
                    <button
                        onClick={handleAdd}
                        className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                        <Plus className="w-4 h-4" />
                        Add Video
                    </button>
                </div>

                {/* Search */}
                <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                    <div className="relative max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search videos..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="pl-10 pr-4 py-2 border rounded-lg text-sm w-full"
                        />
                    </div>
                </div>

                {/* Videos Grid */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center text-gray-500">Loading...</div>
                    ) : filteredVideos.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">No videos found</div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
                            {filteredVideos.map(video => (
                                <div key={video.id} className="border rounded-lg overflow-hidden hover:shadow-md transition">
                                    <div className="relative aspect-[9/16] bg-gray-100">
                                        <img
                                            src={video.thumbnail_url || getYoutubeThumbnail(video.video_url)}
                                            alt={video.title}
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/300x533?text=Video' }}
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                                        <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                                            <p className="font-medium text-sm line-clamp-2">{video.title || 'Untitled'}</p>
                                            <div className="flex items-center gap-3 mt-2 text-xs text-gray-300">
                                                <span className="flex items-center gap-1">
                                                    <Eye className="w-3 h-3" />
                                                    {formatNumber(video.views)}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <Heart className="w-3 h-3" />
                                                    {formatNumber(video.likes)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="absolute top-2 right-2 flex gap-1">
                                            <a
                                                href={video.video_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="p-1.5 bg-black/50 rounded text-white hover:bg-black/70"
                                            >
                                                <ExternalLink className="w-3 h-3" />
                                            </a>
                                        </div>
                                        {/* Linked Item Badge */}
                                        {video.linked_item_type && video.linked_item_type !== 'none' && (
                                            <div className="absolute top-2 left-2 flex items-center gap-1 bg-white/90 px-2 py-1 rounded text-xs">
                                                {getLinkedItemIcon(video.linked_item_type)}
                                                <span className="capitalize">{video.linked_item_type} #{video.linked_item_id}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-3 flex justify-between items-center bg-gray-50">
                                        <span className={`px-2 py-0.5 rounded-full text-xs ${video.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                            {video.status}
                                        </span>
                                        <div className="flex gap-1">
                                            <button onClick={() => handleEdit(video)} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded">
                                                <Edit className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => deleteVideo(video.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold">{editingVideo ? 'Edit Video' : 'Add New Video'}</h2>
                            <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">YouTube URL *</label>
                                <input
                                    type="url"
                                    placeholder="https://www.youtube.com/watch?v=... or /shorts/..."
                                    className="w-full px-3 py-2 border rounded-lg"
                                    value={formData.video_url}
                                    onChange={e => handleVideoUrlChange(e.target.value)}
                                    required
                                />
                                <p className="text-xs text-gray-400 mt-1">Shorts URLs will be auto-converted to watch URLs</p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Title (English)</label>
                                    <input
                                        type="text"
                                        placeholder="Video title"
                                        className="w-full px-3 py-2 border rounded-lg"
                                        value={formData.title_en}
                                        onChange={e => setFormData({ ...formData, title_en: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Title (Vietnamese)</label>
                                    <input
                                        type="text"
                                        placeholder="Tiêu đề video"
                                        className="w-full px-3 py-2 border rounded-lg"
                                        value={formData.title_vi}
                                        onChange={e => setFormData({ ...formData, title_vi: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Status</label>
                                <select
                                    className="w-full px-3 py-2 border rounded-lg"
                                    value={formData.status}
                                    onChange={e => setFormData({ ...formData, status: e.target.value })}
                                >
                                    <option value="published">Published</option>
                                    <option value="draft">Draft</option>
                                </select>
                            </div>

                            <div className="border-t pt-4">
                                <h3 className="font-medium mb-3 flex items-center gap-2">
                                    <LinkIcon className="w-4 h-4" />
                                    Link to Product (Optional)
                                </h3>
                                <p className="text-sm text-gray-500 mb-3">
                                    Link this video to a tour, vehicle, or article to show at the bottom of the video.
                                </p>

                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Item Type</label>
                                        <select
                                            className="w-full px-3 py-2 border rounded-lg"
                                            value={formData.linked_item_type}
                                            onChange={e => {
                                                setFormData({
                                                    ...formData,
                                                    linked_item_type: e.target.value,
                                                    linked_item_id: '',
                                                    linked_item_name: ''
                                                })
                                                setLinkedItemSearch('')
                                                setLinkedItemResults([])
                                            }}
                                        >
                                            <option value="none">None</option>
                                            <option value="tour">🗺️ Tour</option>
                                            <option value="vehicle">🚗 Vehicle</option>
                                            <option value="article">📰 Article</option>
                                        </select>
                                    </div>

                                    {formData.linked_item_type !== 'none' && (
                                        <div className="relative">
                                            <label className="block text-sm font-medium mb-1">
                                                Search {formData.linked_item_type}
                                            </label>

                                            {formData.linked_item_id ? (
                                                <div className="flex items-center gap-2 p-2 bg-gray-50 border rounded-lg">
                                                    {getLinkedItemIcon(formData.linked_item_type)}
                                                    <span className="flex-1 text-sm">{formData.linked_item_name}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData({ ...formData, linked_item_id: '', linked_item_name: '' })}
                                                        className="p-1 hover:bg-gray-200 rounded"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="relative">
                                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                                        <input
                                                            type="text"
                                                            placeholder={`Search ${formData.linked_item_type}s...`}
                                                            className="w-full pl-10 pr-4 py-2 border rounded-lg"
                                                            value={linkedItemSearch}
                                                            onChange={e => setLinkedItemSearch(e.target.value)}
                                                        />
                                                        {searchingLinkedItems && (
                                                            <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                                                        )}
                                                    </div>

                                                    {linkedItemResults.length > 0 && (
                                                        <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                                            {linkedItemResults.map(item => (
                                                                <button
                                                                    key={item.id}
                                                                    type="button"
                                                                    onClick={() => selectLinkedItem(item)}
                                                                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-left text-sm"
                                                                >
                                                                    {getLinkedItemIcon(item.type)}
                                                                    <span className="flex-1">{item.name}</span>
                                                                    <span className="text-xs text-gray-400">#{item.id}</span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-end gap-2 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 border rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
                                >
                                    <Save className="w-4 h-4" />
                                    {saving ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    )
}
